import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import './BlogDetails.css';
import SEO from '../components/SEO';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://api.sofiasurgicals.com';
const defaultImage = '../assets/images/00cfb8d7d1e59f2b9c0ee9e3307da7a2.png';

const stripHtml = (html) => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
};

const BlogDetail = () => {
    const navigate = useNavigate();
    const { id: slug } = useParams();

    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    useEffect(() => {
        if (!slug) {
            setLoading(false);
            setError('Blog not found.');
            return;
        }

        const controller = new AbortController();

        const fetchBlog = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await axios.get(
                    `${BASE_URL}/api/get-blog-by-slug/${slug}`,
                    { signal: controller.signal }
                );
                const data = response.data?.data || response.data;
                if (data) {
                    setBlog(data);
                } else {
                    setError('Blog not found.');
                }
            } catch (err) {
                if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
                    setError('Blog not found.');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchBlog();
        return () => controller.abort();
    }, [slug]);

    const getImageUrl = (imagePath) =>
        imagePath ? `${BASE_URL}/${imagePath}` : defaultImage;

    // Loading skeleton
    if (loading) {
        return (
            <div className="blog-section">
                <SEO
                    title="Loading Blog..."
                    description="Sofia Surgicals blog article loading..."
                    url={`/blogs/${slug || ''}`}
                />
                <div style={{ maxWidth: 800, margin: '40px auto', padding: '0 20px' }}>
                    <div style={{
                        height: 300, width: '100%', borderRadius: 12, marginBottom: 24,
                        backgroundImage: 'linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)',
                        backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite',
                    }} />
                    <div style={{
                        height: 36, width: '65%', borderRadius: 6, marginBottom: 16,
                        backgroundImage: 'linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)',
                        backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite',
                    }} />
                    {[90, 80, 95, 70, 85].map((w, i) => (
                        <div key={i} style={{
                            height: 16, width: `${w}%`, borderRadius: 4, marginBottom: 12,
                            backgroundImage: 'linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)',
                            backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite',
                        }} />
                    ))}
                </div>
                <style>{`@keyframes shimmer { to { background-position: -200% 0; } }`}</style>
            </div>
        );
    }

    // Error state
    if (error || !blog) {
        return (
            <div className="blog-section" style={{ textAlign: 'center', padding: '60px 20px' }}>
                <SEO
                    title="Blog Not Found"
                    description="The requested blog post could not be found on Sofia Surgicals."
                    url={`/blogs/${slug || ''}`}
                />
                <p style={{ fontSize: '1.2rem', color: '#555', marginBottom: 24 }}>
                    {error || 'Blog not found.'}
                </p>
                <button onClick={() => navigate('/blogs')} className="back-btn">
                    ← Back to All Blogs
                </button>
            </div>
        );
    }

    // Render with dynamic SEO
    const pageTitle = blog.seoTitle || blog.title;
    const description = blog.metaDescription || blog.snippet || '';
    const keywords = stripHtml(blog.metaKeywords || '');
    const imageUrl = getImageUrl(blog.image);
    const canonicalUrl = `/blogs/${blog.slug || slug}`;

    return (
        <div className="blog-section">
            <SEO
                title={pageTitle}
                description={description}
                keywords={keywords}
                image={imageUrl}
                url={canonicalUrl}
                type="article"
            />

            <div className="blog-detail">
                <button onClick={() => navigate('/blogs')} className="back-btn">
                    ← Back to All Blogs
                </button>

                <h1 className="blog-detail-title">{blog.title}</h1>

                {blog.createdAt && (
                    <p className="blog-detail-date">
                        {new Date(blog.createdAt).toLocaleDateString('en-IN', {
                            day: 'numeric', month: 'long', year: 'numeric',
                        })}
                    </p>
                )}

                <img
                    src={imageUrl}
                    alt={blog.title}
                    className="blog-detail-img"
                    loading="eager"
                />

                <div
                    className="blog-detail-content ck-content"
                    dangerouslySetInnerHTML={{ __html: blog.content }}
                />

                <div className="blog-detail-footer">
                    <button onClick={() => navigate('/blogs')} className="back-btn">
                        ← Back to All Blogs
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BlogDetail;